<template>
  <BaseModule
    v-bind="$props"
    class="module-links-footer"
    data-module="links-footer"
    :style="{
      '--links-color': linksColor,
    }"
  >
    <footer class="footer">
      <div class="container footer__main">
        <div class="identity">
          <LImage
            v-if="footerLogo"
            :src="footerLogo"
            :alt="footerLogoAlt"
            :no-stretch="true"
            loader-size="24"
          />
          <div
            v-if="text"
            class="intro-text"
            v-html="text"
          ></div>

          <div class="socials">
            <ul class="socials__list">
              <li
                v-for="(socialObj, index) in socialLinks"
                :key="index"
                class="socials__list__item"
              >
                <EditorLink
                  v-if="showIcon(socialObj)"
                  :model="socialObj.linkURL"
                  class="socials__list__item__link"
                  :title="socialObj.title"
                >
                  <component
                    :is="socialObj.socialType"
                    :color="textColor"
                    width="30"
                    height="30"
                  />
                </EditorLink>
              </li>
            </ul>
          </div>
        </div>

        <div class="numbered-columns">
          <div
            v-if="columnOneTitle || showColumnLinks(columnOneLinks)"
            id="column-one"
            class="column links"
          >
            <h3
              v-if="columnOneTitle"
              class="footer-title"
              :style="{ borderColor: textColor }"
            >
              {{ columnOneTitle }}
            </h3>
            <ul
              v-if="showColumnLinks(columnOneLinks)"
              class="links__list"
            >
              <li
                v-for="(link, index) in columnOneLinks"
                :key="index"
                class="links__list__item"
              >
                <EditorLink
                  :model="link.linkUrl"
                  class="links__list__item__link"
                >
                  {{ link.linkText }}
                </EditorLink>
              </li>
            </ul>
          </div>

          <div
            v-if="columnTwoTitle || showColumnLinks(columnTwoLinks)"
            id="column-two"
            class="column links"
          >
            <h3
              v-if="columnTwoTitle"
              class="footer-title"
              :style="{ borderColor: textColor }"
            >
              {{ columnTwoTitle }}
            </h3>
            <ul
              v-if="showColumnLinks(columnTwoLinks)"
              class="links__list"
            >
              <li
                v-for="(link, index) in columnTwoLinks"
                :key="index"
                class="links__list__item"
              >
                <EditorLink
                  :model="link.linkUrl"
                  class="links__list__item__link"
                >
                  {{ link.linkText }}
                </EditorLink>
              </li>
            </ul>
          </div>

          <div
            v-if="columnThreeTitle || showColumnLinks(menuLinks)"
            id="column-three"
            class="column links"
          >
            <h3
              v-if="columnThreeTitle"
              class="footer-title"
              :style="{ borderColor: textColor }"
            >
              {{ columnThreeTitle }}
            </h3>
            <ul
              v-if="showColumnLinks(menuLinks)"
              class="links__list"
            >
              <li
                v-for="(link, index) in menuLinks"
                :key="index"
                class="links__list__item"
              >
                <EditorLink
                  :model="link.linkUrl"
                  class="links__list__item__link"
                >
                  {{ link.linkText }}
                </EditorLink>
                <ul class="links__list">
                  <li
                    v-for="(sublink, subIndex) in link.menuLinks"
                    :key="subIndex"
                    class="links__list__item"
                  >
                    <EditorLink
                      :model="sublink.linkUrl"
                      class="links__list__item__link"
                    >
                      {{ sublink.linkText }}
                    </EditorLink>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div
        class="footer__divider"
        :style="{ borderColor: dividerColor }"
      ></div>

      <div
        class="footer__bottom"
        :class="{ center: !showFlags && !bottomText }"
      >
        <div
          v-if="showFlags"
          class="column footer__bottom__flags"
        >
          <ul class="footer__bottom__flags__list">
            <li
              v-for="(flag, index) in filteredFlags"
              :key="index"
              class="footer__bottom__flags__list__item"
            >
              <LImage
                class="footer__bottom__flags__list__item__flag"
                :src="`/payment/${flag.imgName}.png`"
                :alt="flag.name"
              />
            </li>
          </ul>
        </div>
        <div
          v-if="bottomText"
          class="column footer__bottom__text"
          :class="{ left: !showFlags }"
          v-html="bottomText"
        ></div>
        <div
          class="column footer__bottom__letsbook"
          :class="{ center: !showFlags && !bottomText }"
        >
          Powered by
          <span class="logo">
            <a
              href="https://letsbook.com.br"
              target="_blank"
            >
              <Logo
                :width="null"
                :height="null"
                :color="textColor"
              />
            </a>
          </span>
        </div>
      </div>
    </footer>
  </BaseModule>
</template>

<!-- eslint-disable vue/no-unused-components -->
<script>
import { mapGetters } from 'vuex';

import flagsPayment from '@/helpers/flagsPayment.json';
import IconLinkRound from '@/components/icons/IconLinkRound.vue';
import IconWhatsAppRound from '@/components/icons/IconWhatsAppRound.vue';
import IconFacebook from '@/components/icons/IconFacebook.vue';
import IconInstagram from '@/components/icons/IconInstagram.vue';
import IconYoutube from '@/components/icons/IconYoutube.vue';
import IconLinkedin from '@/components/icons/IconLinkedin.vue';
import IconTripAdvisor from '@/components/icons/IconTripAdvisor.vue';
import IconTwitter from '@/components/icons/IconTwitter.vue';
import IconSpotify from '@/components/icons/IconSpotify.vue';
import IconTikTok from '@/components/icons/IconTikTok.vue';
import IconLogo from '@/components/icons/IconLogo.vue';
import getApiHotels from '@/mixins/getApiHotels';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

const tinycolor = require('tinycolor2');

export default {
  name: 'LinksFooter',
  components: {
    BaseModule,
    EditorLink,
    facebookLink: IconFacebook,
    instagramLink: IconInstagram,
    youTubeLink: IconYoutube,
    linkedinLink: IconLinkedin,
    tripAdvisorLink: IconTripAdvisor,
    twitterLink: IconTwitter,
    spotifyLink: IconSpotify,
    tiktokLink: IconTikTok,
    whatsLink: IconWhatsAppRound,
    genericLink: IconLinkRound,
    Logo: IconLogo,
  },
  mixins: [getApiHotels],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    linksColor: {
      type: String,
      default: '',
    },
    textColor: {
      type: String,
      default: '',
    },
    footerLogo: {
      type: String,
      default: 'https://letsbook.com.br/images/logo-letsbook-principal-v2.png',
    },
    footerLogoAlt: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    socialLinks: {
      type: Array,
      default() {
        return [];
      },
    },
    columnOneTitle: {
      type: String,
      default: 'Coluna 1',
    },
    columnOneLinks: {
      type: Array,
      default: () => [],
    },
    columnTwoTitle: {
      type: String,
      default: 'Coluna 2',
    },
    columnTwoLinks: {
      type: Array,
      default: () => [],
    },
    columnThreeTitle: {
      type: String,
      default: 'Coluna 3',
    },
    menuLinks: {
      // links da 'columnThree'. esta prop precisa deste nome para funcionar com menus globais
      type: Array,
      default: () => [],
    },
    flagsOption: {
      type: String,
      default: 'none',
    },
    hotelId: {
      type: Number,
      default: 0,
    },
    bottomText: {
      type: String,
      default: '',
    },
    titleSizeDesk: {
      type: Number,
      default: 16,
    },
    titleSizeMobile: {
      type: Number,
      default: 16,
    },
    contentSizeDesk: {
      type: Number,
      default: 14,
    },
    contentSizeMobile: {
      type: Number,
      default: 14,
    },
  },
  data() {
    return {
      flags: [],
    };
  },
  computed: {
    dividerColor() {
      return this.darken(this.backgroundColor, 8);
    },
    showFlags() {
      return (
        (this.flagsOption === 'chain' || this.flagsOption === 'hotel') &&
        this.flags.length > 0
      );
    },
    filteredFlags() {
      return this.flags.reduce((acc, flag) => {
        const mappedFlag = flagsPayment.find(
          (item) => item.name.toLowerCase() === flag.NomeCartao.toLowerCase(),
        );

        if (mappedFlag) {
          acc.push(mappedFlag);
        }

        return acc;
      }, []);
    },
    ...mapGetters(['settings', 'hasLetsbookIntegration']),
  },
  watch: {
    flagsOption(option) {
      this.flags = [];

      if (option === 'chain') {
        this.requestFlags();
      } else if (option === 'hotel' && typeof this.hotelId === 'number') {
        this.requestFlags(true);
      }
    },
    hotelId(val) {
      if (typeof val === 'number' && this.flagsOption === 'hotel') {
        this.requestFlags(true);
      }
    },
  },
  mounted() {
    if (this.hasLetsbookIntegration) {
      this.getApiHotels();

      if (this.flagsOption === 'chain') {
        this.requestFlags();
      } else if (
        this.flagsOption === 'hotel' &&
        typeof this.hotelId === 'number'
      ) {
        this.requestFlags(true);
      }
    }
  },
  methods: {
    darken(color, amt) {
      return tinycolor(color).darken(amt).toString();
    },
    showColumnLinks(columnLinks) {
      return Array.isArray(columnLinks) && columnLinks.length > 0;
    },
    showIcon(socialObj) {
      return socialObj.socialType && socialObj.linkURL?.value;
    },
    async requestFlags(byHotel) {
      this.flags = [];
      const urlFlags = `${this.settings.letsbookApiId}/${
        byHotel
          ? `formasPagamentoHotel/${this.hotelId}`
          : `formasPagamentoLanding/${this.settings.letsbookLandingPageId}`
      }`;

      if (!this.apiData[urlFlags]) {
        try {
          await this.$store.dispatch('loadApiData', urlFlags);
        } catch (error) {
          console.log(error);
        }
      }
      this.flags = this.apiData[urlFlags];
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'footerContent',
            label: 'Conteúdo',
            fields: [
              {
                name: 'footerLogo',
                type: 'ImageFile',
                label: 'Logo',
                maxWidth: 300,
                maxHeight: 90,
                resizeInsteadCrop: true,
                helpText: 'Tamanho máximo 300x90, formato .PNG',
                enableRemoval: true,
              },
              {
                name: 'footerLogoAlt',
                type: 'ShortText',
                label: 'Texto alternativo (para SEO)',
              },
              {
                name: 'text',
                type: 'Html',
                label: 'Texto',
              },
              {
                name: 'socialLinks',
                type: 'List',
                label: 'Redes Sociais',
                itemLabel: 'Link Social {index}',
                addItemLabel: 'Adicionar Link Social',
                fields: [
                  {
                    name: 'socialType',
                    type: 'VueSelect',
                    label: 'Tipo de Link Social',
                    options: [
                      {
                        label: 'Instagram',
                        value: 'instagramLink',
                      },
                      {
                        label: 'YouTube',
                        value: 'youTubeLink',
                      },
                      {
                        label: 'Facebook',
                        value: 'facebookLink',
                      },
                      {
                        label: 'LinkedIn',
                        value: 'linkedinLink',
                      },
                      {
                        label: 'TripAdvisor',
                        value: 'tripAdvisorLink',
                      },
                      {
                        label: 'Twitter',
                        value: 'twitterLink',
                      },
                      {
                        label: 'Spotify',
                        value: 'spotifyLink',
                      },
                      {
                        label: 'TikTok',
                        value: 'tiktokLink',
                      },
                      {
                        label: 'WhatsApp',
                        value: 'whatsLink',
                      },
                      {
                        label: 'Link Externo',
                        value: 'genericLink',
                      },
                    ],
                  },
                  {
                    name: 'title',
                    type: 'ShortText',
                    label: 'Texto alternativo (para SEO)',
                  },
                  {
                    name: 'linkURL',
                    type: 'Url',
                    availableTypes: ['externalLink'],
                  },
                ],
              },
            ],
          },
          {
            name: 'columnOne',
            label: 'Coluna 1',
            fields: [
              {
                name: 'columnOneTitle',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'columnOneLinks',
                type: 'List',
                itemLabel: '{linkText}',
                fields: [
                  {
                    name: 'linkText',
                    type: 'ShortText',
                    label: 'Título',
                  },
                  {
                    name: 'linkUrl',
                    type: 'Url',
                  },
                ],
              },
            ],
          },
          {
            name: 'columnTwo',
            label: 'Coluna 2',
            fields: [
              {
                name: 'columnTwoTitle',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'columnTwoLinks',
                type: 'List',
                itemLabel: '{linkText}',
                fields: [
                  {
                    name: 'linkText',
                    type: 'ShortText',
                    label: 'Título',
                  },
                  {
                    name: 'linkUrl',
                    type: 'Url',
                  },
                ],
              },
            ],
          },
          {
            name: 'columnThree',
            label: 'Coluna 3',
            fields: [
              {
                name: 'columnThreeTitle',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'menuLinks',
                type: 'MenuSelect',
                label: 'Tipo de menu',
                helpText:
                  'Abaixo você poderá escolher entre usar um menu customizado, exclusivo para este módulo, ou usar um menu global reutilizável através de módulos e páginas. Somente esta coluna possui essa função',
                customMenuLabel: 'Itens do menu',
                customMenuHelpText:
                  'Reorganize os itens do menu os arrastando. Crie um submenu movendo um item para dentro de outro.',
                customMenuDefault: [
                  {
                    linkText: 'Pmweb',
                    linkUrl: {
                      type: 'externalLink',
                      value: '',
                      openInNewTab: true,
                    },
                    columnThreeLinks: [],
                  },
                ],
              },
            ],
          },
          {
            name: 'bottomOptions',
            label: 'Área Inferior',
            fields: [
              {
                name: 'flagsOption',
                type: 'VueSelect',
                label: 'Formas de Pagamento',
                isEnabled({ hasLetsbookIntegration }) {
                  return hasLetsbookIntegration;
                },
                options: [
                  {
                    label: 'Nenhuma',
                    value: 'none',
                  },
                  {
                    label: 'Formas de pagamento da Rede',
                    value: 'chain',
                  },
                  {
                    label: 'Formas de pagamento do Hotel',
                    value: 'hotel',
                  },
                ],
              },
              {
                name: 'hotelId',
                type: 'VueSelect',
                label: 'Selecione um hotel',
                isEnabled({ flagsOption }) {
                  return flagsOption === 'hotel';
                },
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
              {
                name: 'bottomText',
                type: 'Html',
                label: 'Texto da área inferior',
              },
            ],
          },
        ],
        toRemove: ['text'],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'linksColor',
                type: 'ColorPicker',
                label: 'Cor dos links',
              },
            ],
            toRemove: ['titleColor', 'buttonColor', 'buttonTextColor'],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.module-links-footer {
  font-size: $size-small;
  color: var(--text-color);

  .footer {
    .container {
      max-width: 100%;
    }

    .column {
      vertical-align: top;
      width: calc(100% / 3);

      @include media-breakpoint-down(lg) {
        width: 100%;
        margin-top: 32px;
      }
    }

    &__main {
      padding: 0 5%;
      padding-top: var(--padding-top);
      padding-bottom: var(--padding-bottom);
      display: flex;
      flex-wrap: nowrap;

      @include media-breakpoint-down(lg) {
        width: 100%;
        flex-direction: column;
        padding-top: var(--padding-top-mobile);
        padding-bottom: var(--padding-bottom-mobile);
      }

      .identity {
        display: inline-block;
        vertical-align: top;
        width: 35%;
        margin-right: 5%;

        .intro-text {
          margin-top: 20px;

          ::v-deep {
            p {
              font-size: var(--content-size-desk);
              line-height: 1.5;
            }
          }
        }

        .socials {
          margin-top: 20px;

          &__list {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;

            &__item {
              &__link {
                width: 30px;
              }
            }
          }
        }

        @include media-breakpoint-down(lg) {
          width: 100%;

          .intro-text {
            ::v-deep {
              p {
                font-size: var(--content-size-mobile);
                line-height: 1.5;
              }
            }
          }
        }
      }

      .numbered-columns {
        display: flex;
        justify-content: flex-end;
        vertical-align: top;
        width: 60%;

        .footer-title {
          font-size: var(--title-size-desk);
          font-style: normal;
          font-weight: $lb-bold;
          text-align: left;
        }

        .links {
          text-align: left;
          position: relative;

          &:not(:last-child) {
            margin-right: 16px;

            @include media-breakpoint-down(lg) {
              margin-right: 0;
            }
          }

          &__list {
            &__item {
              font-size: var(--content-size-desk);
              line-height: 1em;
              margin-top: 16px;

              &__link {
                font-size: 1em;
                color: var(--links-color);

                :hover {
                  text-decoration: underline;
                }
              }

              li {
                margin-left: 12px;
              }
            }
          }
        }

        @include media-breakpoint-down(lg) {
          width: 100%;
          flex-direction: column;
          justify-content: center;
          padding: 0 auto;

          .footer-title {
            font-size: var(--title-size-desk);
          }

          .links {
            text-align: left;
            position: relative;

            &__list {
              &__item {
                font-size: var(--content-size-mobile);
              }
            }
          }
        }
      }
    }

    &__divider {
      margin: 0 5%;
      border: 1px solid;
    }

    &__bottom {
      display: flex;
      padding: 30px 0;
      margin: 0 5%;
      justify-content: space-between;

      @include media-breakpoint-down(lg) {
        flex-direction: column;
        gap: 20px;
      }

      .column {
        margin-top: auto;
        margin-bottom: auto;
      }

      &__flags {
        width: 25% !important;
        text-align: left;
        font-weight: $lb-bold;

        @include media-breakpoint-down(lg) {
          width: 100% !important;
        }

        &__list {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;

          @include media-breakpoint-down(lg) {
            justify-content: center;
          }

          &__item {
            &__flag {
              width: 40px;
            }
          }
        }
      }

      &__text {
        width: 40% !important;
        flex-wrap: wrap;
        text-align: center;
        padding: 0 5%;

        &.left {
          @include media-breakpoint-up(lg) {
            padding-left: 0;
            text-align: left;
          }
        }

        @include media-breakpoint-down(lg) {
          text-align: center;
          width: 100% !important;
          height: auto;
          flex-direction: column;
        }
      }

      &__letsbook {
        width: 25% !important;
        font-size: 12px;
        font-weight: $lb-bold;
        display: flex;
        justify-content: flex-end;

        @include media-breakpoint-down(lg) {
          width: 100% !important;
          height: auto;
          justify-content: center;
        }

        .logo {
          width: 70px;
          max-height: 50px;
          margin-left: 15px;
        }
      }
    }

    .center {
      justify-content: center !important;
    }
  }
}
</style>
